import { Editor } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import StarterKit from "@tiptap/starter-kit";
import { register } from "../app";

type ToolbarButton = {
    command: string;
    icon: string;
    title: string;
    active?: string | Record<string, string>;
    args?: string;
    prompt?: boolean;
    chain?: string;
};

type ToolbarGroup = {
    group: ToolbarButton[];
};

const buttons: ToolbarGroup[] = [
    {
        group: [
            { command: "toggleBold", icon: "bi-type-bold", active: "bold", title: "Gras" },
            { command: "toggleItalic", icon: "bi-type-italic", active: "italic", title: "Italique" },
        ]
    },
    {
        group: [
            { command: "setLink", icon: "bi-link-45deg", active: "link", prompt: true, title: "Lien" },
        ]
    },
    {
        group: [
            { command: "setTextAlign", icon: "bi-text-left", active: { textAlign: "left" }, args: "left", title: "Aligner à gauche" },
            { command: "setTextAlign", icon: "bi-text-center", active: { textAlign: "center" }, args: "center", title: "Centrer" },
            { command: "setTextAlign", icon: "bi-text-right", active: { textAlign: "right" }, args: "right", title: "Aligner à droite" },
            { command: "setTextAlign", icon: "bi-justify", active: { textAlign: "justify" }, args: "justify", title: "Justifier" },
        ]
    },
    {
        group: [
            { command: "toggleBulletList", icon: "bi-list-ul", active: "bulletList", title: "Liste à puces" },
            { command: "toggleOrderedList", icon: "bi-list-ol", active: "orderedList", title: "Liste numérotée" },
        ]
    },
    {
        group: [
            { command: "setHorizontalRule", icon: "bi-hr", title: "Ligne horizontale" },
        ]
    },
    {
        group: [
            { command: "unsetAllMarks", icon: "bi-eraser", chain: "clearNodes", title: "Supprimer le formatage" },
        ]
    },
];

const createToolbar = (editor: Editor): HTMLDivElement => {
    const toolbar = document.createElement("div");
    toolbar.className = "tiptap-toolbar";

    for (const { group } of buttons) {
        const groupEl = document.createElement("span");
        groupEl.className = "tiptap-toolbar-group";

        for (const btn of group) {
            const button = document.createElement("button");
            button.type = "button";
            button.className = "tiptap-toolbar-btn";

            button.title = btn.title;

            const icon = document.createElement("i");
            icon.className = `bi ${btn.icon}`;
            button.appendChild(icon);

            button.addEventListener("click", e => {
                e.preventDefault();

                if (btn.prompt) {
                    const previousUrl = editor.getAttributes("link").href as string | undefined;
                    const url = window.prompt("URL", previousUrl || "https://");
                    if (null === url) return;
                    if ("" === url) {
                        editor.chain().focus().extendMarkRange("link").unsetLink().run();
                    } else {
                        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
                    }

                    return;
                }

                if (btn.chain) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (editor.chain().focus() as any)[btn.command]()[btn.chain]().run();
                } else if (btn.args) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (editor.chain().focus() as any)[btn.command](btn.args).run();
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (editor.chain().focus() as any)[btn.command]().run();
                }
            });

            groupEl.appendChild(button);
        }

        toolbar.appendChild(groupEl);
    }

    const updateActive = () => {
        toolbar.querySelectorAll(".tiptap-toolbar-btn").forEach((button, index) => {
            const flatButtons = buttons.flatMap(b => b.group);
            const btn = flatButtons[index];
            if (!btn?.active) return;

            const isActive = editor.isActive(btn.active);
            button.classList.toggle("is-active", isActive);
        });
    };

    editor.on("selectionUpdate", updateActive);
    editor.on("transaction", updateActive);

    return toolbar;
};

register(container => {
    container.querySelectorAll<HTMLTextAreaElement>("textarea[data-wysiwyg]").forEach(textarea => {
        const wrapper = document.createElement("div");
        wrapper.className = "tiptap-box";
        textarea.parentNode!.insertBefore(wrapper, textarea);

        const editor = new Editor({
            element: wrapper,
            extensions: [
                StarterKit,
                Link.configure({ openOnClick: false }),
                TextAlign.configure({ types: [ "paragraph" ] }),
                Image,
            ],
            content: textarea.value,
            editable: !textarea.disabled,
            onUpdate: ({ editor: ed }) => {
                textarea.value = ed.getHTML();
            },
        });

        if (!textarea.disabled) {
            const toolbar = createToolbar(editor);
            wrapper.insertBefore(toolbar, wrapper.firstChild);
        }
    });
});
